import { default as React, useEffect } from 'react';
import { Component } from '../type-extensions';
import { safeUseState } from '../hooks/safe-use-state';

/**
 * initialize viewmodel using viewModelFactoryFunction
 * ensure that wrapped component is rendered 'only' after viewModel is initialized
 * @param WrappedComponent - component which rely on the viewmodel and aceptt it in the 'vm' prop
 * @param viewModelFactory - function, responsible for viewmodel creation from props. Might return either promise or viewmodel instance
 *  if promise is returned - WrappedComponent is rendered only after this promise is fulfilled
 * @param depsSelector - convert props into array of dependencies (react useEffect 'deps' argument). If changed - re-create viewmodel using viewModelFactory
 * @param shutdownViewModel - function, responsible for cleaning up viewmodel resources (if any)
 */
const withViewModel = <TProps, TViewModelType>(
    WrappedComponent: Component<{ vm: TViewModelType }>,
    viewModelFactory: (props: TProps) => Promise<TViewModelType> | TViewModelType,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    depsSelector?: (props: TProps) => any[],
    shutdownViewModel?: (vm: TViewModelType) => void,
) => {
    return (props: TProps) => {
        const [viewModel, setViewModel] = safeUseState<TViewModelType>(null);

        useEffect(() => {
            const viewModelFactoryResult = viewModelFactory(props);
            // closure is needed in order to cleanup effect
            // we can't use viewModel variable as it is consumed in cleanup function as a closure which is initially set to null
            // and then closure by itself is not updated by calling 'setViewModel'
            let viewModelClosure : TViewModelType = null;

            // if viewModelFactory return promise - await it first, then set viewmodel into component's state
            if (viewModelFactoryResult instanceof Promise) {
                viewModelFactoryResult.then(viewModelInstance => {
                    viewModelClosure = viewModelInstance;
                    setViewModel(viewModelInstance);
                });
            } else {
                // if viewModelFactory return something other than promise - set it as a viewmodel right away
                // in case of invalid input application will crash and it is desired behavior as this shouldn't be handled silently
                viewModelClosure = viewModelFactoryResult;
                setViewModel(viewModelFactoryResult);
            }

            return () => {
                if (shutdownViewModel && viewModelClosure) {
                    shutdownViewModel(viewModelClosure);
                }
            };
        }, depsSelector ? depsSelector(props) : []);

        if (viewModel) {
            return <WrappedComponent vm={viewModel} />;
        } else {
            return null;
        }
    };
};

export { withViewModel };